import {Component, OnInit, ViewChild} from '@angular/core';
import {MatSort, SortDirection} from '@angular/material/sort';
import {FormBuilder, FormControl} from '@angular/forms';
import {debounce, Observable, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {SettingsRestApiService} from '../services/http/settings-rest-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiSettingsListResponse} from '../services/http/api-settings-list-response';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
import {ApiSettingsResponse} from '../services/http/api-settings-response';
import {ApiSettingsListRequest} from '../services/http/api-settings-list-request';
import {ApiSettingsEditRequest} from '../services/http/api-settings-edit-request';
import {Utils} from '@astek/utils';
import {PatientRestApiService} from '../../patient/services/http/patient-rest-api.service';
import {DateTime} from 'luxon';
import {SharedUtils} from '../../../shared/utils/shared-utils';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {MatDialog} from '@angular/material/dialog';
import {WaitDownloadDialogComponent} from '../wait-download-dialog/wait-download-dialog.component';

@Component({
  selector: 'xp-settings-list',
  templateUrl: './settings-list.component.html',
  styleUrls: ['../../crud.common.scss', './settings-list.component.scss'],
})
export class SettingsListComponent implements OnInit {

  public isLoading: boolean = true;
  public settings: Array<ApiSettingsResponse> | null = null;
  public displayedColumns: Array<string> = [
    'label',
    'value',
    'actions',
  ];
  public formGroup = this._formBuilder.group({
    search: new FormControl<string>(''),
  }, {});
  public formSettings = this._formBuilder.group({});
  public defaultSortOn: string = 'id';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _settingsRestApiService: SettingsRestApiService,
    private readonly _patientRestApiService: PatientRestApiService,
    private readonly _formBuilder: FormBuilder,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
    private readonly _dialog: MatDialog
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestSettings().pipe(
        takeUntil(this._requestApiOnChangeSubject),
      )),
    ).subscribe({
      next: (response: ApiSettingsListResponse) => this.handleRequestSettingsResponse(response),
    });
  }

  public get searchValue(): string {
    return this.formGroup.controls.search.value?.trim() ?? '';
  }

  public ngOnInit(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onSearchInput(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.KEYBOARD_INPUT);
  }

  public onSortChange(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public clearSearch(): void {
    this.formGroup.controls.search.setValue('');
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public resetFilters(): void {
    this.formGroup.controls.search.setValue('');
    this.clearSearch();
    this.formGroup.markAsPristine();
  }

  public updateSetting(id: string | null): void {
    if (!id) {
      return;
    }

    const control = this.formSettings.get(id);

    if (Utils.isNullOrUndefined(control)
      || !Utils.isString(control.value)
      || Utils.isBlank(control.value)
      || !control.value
      || control.invalid
    ) {
      return;
    }

    const data: ApiSettingsEditRequest = {
      value: control.value,
    };


    this.requestUpdateSettings(id, data).subscribe({
      next: () => this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY),
      error: () => this.handleError(id),
    });
  }

  public downloadCase(): void {
    const dialogRef = this._dialog.open(WaitDownloadDialogComponent, {
      width: '400px',
      disableClose: true
    });

    this._patientRestApiService.requestCaseExport().subscribe({
      next: (response: Blob) => {
        const dateStr: string = DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss');
        const filename = `case_export_${dateStr}.zip`;
        SharedUtils.forceDownload(response, filename);

        dialogRef.close();
      },
      error: () => {
        dialogRef.close();
        this._snackBar.open(
          this._translocoService.translate('SETTINGS.LIST.DOWNLOAD_DATABASE_ERROR'),
          '',
          { duration: 7000 }
        );
      }
    });
  }

  private requestSettings(): Observable<ApiSettingsListResponse> {
    this.isLoading = true;
    const queryParams: ApiSettingsListRequest = {
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
      search: this.searchValue,
    };
    return this._settingsRestApiService.requestSettingsList(queryParams);
  }

  private requestUpdateSettings(id: string, data: ApiSettingsEditRequest): Observable<void> {
    this.isLoading = true;

    return this._settingsRestApiService.requestUpdate(id, data);
  }

  private handleRequestSettingsResponse(response: ApiSettingsListResponse): void {
    this.isLoading = false;
    this.settings = response.settings;
    const controls: { [key: string]: FormControl } = {};

    this.settings.forEach((setting) => {
      controls[setting.id] = new FormControl<string>(setting.value);
    });

    this.formSettings = this._formBuilder.group(controls);
  }

  private handleError(id: string): void {
    this.isLoading = false;
    const control = this.formSettings.get(id);
    control?.setErrors({fromApi: true});
  }
}
